import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Spinner,
} from '@/components/ui';
import {
  approveMovement,
  cancelMovement,
  emergencyBypassMovement,
  getMovement,
  rejectMovement,
  submitMovement,
  type Movement,
  type MovementApprovalStep,
} from '@/api/services/movements.service';
import { listHrStructure } from '@/api/services/hr-structure.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { usePermissions } from '@/hooks/usePermissions';
import { MovementStatusBadge } from './MovementStatusBadge';
import {
  formatAssignmentValue,
  formatCompensation,
  movementFieldLabel,
  movementTypeLabel,
} from './movementFormat';

function formatInstant(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

const STEP_LABELS: Record<MovementApprovalStep['role'], string> = {
  manager: 'Reporting manager',
  hr: 'Company HR',
  admin: 'Organization admin',
};

function ApprovalChain({ steps }: { steps: MovementApprovalStep[] }): React.ReactElement {
  return (
    <ol className="divide-y divide-gray-200 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
      {steps.map((step, index) => (
        <li key={`${step.role}-${index}`} className="flex flex-wrap items-center justify-between gap-2 p-3">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {STEP_LABELS[step.role]}
            </p>
            {step.reason && (
              <p className="text-xs text-gray-600 dark:text-gray-300">{step.reason}</p>
            )}
          </div>
          <div className="text-right">
            <span className="text-xs capitalize text-gray-700 dark:text-gray-200">
              {step.status}
            </span>
            {step.decidedAt && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatInstant(step.decidedAt)}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Before → after for every changed field. Compensation is only rendered when
 * the server included it (it is stripped for callers without `hr:directory`).
 */
function MovementChanges({
  movement,
  nameById,
}: {
  movement: Movement;
  nameById: ReadonlyMap<string, string>;
}): React.ReactElement {
  const fields = Array.isArray(movement.changedFields)
    ? movement.changedFields.filter((field): field is string => typeof field === 'string')
    : [];
  if (fields.length === 0) {
    return (
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">No field changes recorded.</p>
    );
  }
  const hasCompensation = Boolean(movement.compensationBefore || movement.compensationAfter);
  return (
    <dl className="mt-2 divide-y divide-gray-200 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
      {fields.map((field) => {
        const isCompensation = field === 'compensation';
        if (isCompensation && !hasCompensation) {
          return (
            <div key={field} className="p-3">
              <dt className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {movementFieldLabel(field)}
              </dt>
              <dd className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
                Pay details are hidden for your role.
              </dd>
            </div>
          );
        }
        const before = isCompensation
          ? formatCompensation(movement.compensationBefore)
          : formatAssignmentValue(field, movement.before, nameById);
        const after = isCompensation
          ? formatCompensation(movement.compensationAfter)
          : formatAssignmentValue(field, movement.after, nameById);
        return (
          <div key={field} className="p-3">
            <dt className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {movementFieldLabel(field)}
            </dt>
            <dd className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 text-sm text-gray-700 dark:text-gray-200">
              <span className="break-words text-gray-500 dark:text-gray-400">
                {before}
              </span>
              <span aria-hidden className="text-gray-400">→</span>
              <span className="sr-only">changed to</span>
              <span className="break-words font-medium">{after}</span>
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export function MovementDetail({
  companyId,
  movementId,
  canBypass,
  onClose,
}: {
  companyId: string;
  movementId: string;
  canBypass: boolean;
  onClose: () => void;
}): React.ReactElement {
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['hr-movement', companyId, movementId],
    queryFn: () => getMovement(companyId, movementId),
  });
  const { can } = usePermissions();
  // Structure names turn assignment ids into labels; without access the
  // change list falls back to short references.
  const { data: structure } = useQuery({
    queryKey: ['hr-structure', companyId],
    queryFn: () => listHrStructure(companyId),
    enabled: can('hr', 'structure'),
  });
  const nameById = useMemo(
    () =>
      new Map(
        (Array.isArray(structure) ? structure : []).map((row) => [row._id, row.name] as const)
      ),
    [structure]
  );

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['hr-movements'] });
    void queryClient.invalidateQueries({ queryKey: ['hr-movement', companyId, movementId] });
  };

  const mutation = useMutation({
    mutationFn: async (command: 'submit' | 'approve' | 'reject' | 'cancel' | 'bypass') => {
      if (command === 'submit') return submitMovement(companyId, movementId);
      if (command === 'approve') return approveMovement(companyId, movementId);
      if (command === 'reject') return rejectMovement(companyId, movementId, reason);
      if (command === 'cancel') return cancelMovement(companyId, movementId, reason);
      return emergencyBypassMovement(companyId, movementId, reason);
    },
    onSuccess: (movement) => {
      toast.success(`Movement is now ${movement.status.replaceAll('_', ' ')}`);
      setReason('');
      invalidate();
    },
    onError: (mutationError) => toast.error(getErrorMessage(mutationError)),
  });

  const reasonValid = reason.trim().length >= 3;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[94dvh] w-[calc(100vw-1rem)] max-w-3xl overflow-y-auto p-0">
        <div className="border-b border-gray-200 px-5 py-4 pr-14 dark:border-gray-700">
          <DialogTitle>Job change</DialogTitle>
          <DialogDescription className="mt-1">
            Approval workflow and effective-dated change
          </DialogDescription>
        </div>
        <div className="space-y-5 p-5">
          {isLoading ? (
            <div className="flex justify-center py-10" role="status" aria-live="polite">
              <Spinner />
              <span className="sr-only">Loading job change</span>
            </div>
          ) : error ? (
            <div className="space-y-3" role="alert">
              <p className="text-sm text-danger">{getErrorMessage(error)}</p>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          ) : data ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <MovementStatusBadge status={data.status} />
                <span className="text-sm text-gray-700 dark:text-gray-200">
                  {movementTypeLabel(data.type)}
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-300">
                  Effective {formatInstant(data.effectiveFrom)}
                </span>
              </div>

              {data.correction && (
                <p
                  role="note"
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-200"
                >
                  History correction: assignments from this date are rewritten on application.
                  Later changes are kept, and replaced records stay in history.
                </p>
              )}
              {data.backdated && data.payrollImpact && (
                <div
                  role="status"
                  className={
                    data.payrollImpact.touchesFinalizedPeriod
                      ? 'rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100'
                      : 'rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-200'
                  }
                >
                  {data.payrollImpact.touchesFinalizedPeriod
                    ? 'This backdated job change affects a finalized payroll period. It cannot apply until payroll records an adjustment.'
                    : 'This backdated movement affects only open payroll periods.'}
                  {(data.payrollImpact.affectedPeriods ?? []).length > 0 && (
                    <span className="block text-xs">
                      Periods: {data.payrollImpact.affectedPeriods.join(', ')}
                    </span>
                  )}
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Changes
                </h3>
                <MovementChanges movement={data} nameById={nameById} />
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Approval chain
                </h3>
                {!Array.isArray(data.approvalChain) || data.approvalChain.length === 0 ? (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    The chain is resolved when the movement is submitted.
                  </p>
                ) : (
                  <ApprovalChain steps={data.approvalChain} />
                )}
                {data.emergencyBypass && (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                    Remaining steps were bypassed under emergency authority.
                  </p>
                )}
              </div>

              {data.failureReason && (
                <p className="text-sm text-danger" role="alert">
                  Application failed: {data.failureReason}
                </p>
              )}

              <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                <Input
                  id="movement-reason"
                  label="Reason (required to reject, cancel, or bypass)"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  {data.status === 'draft' && (
                    <Button
                      size="sm"
                      loading={mutation.isPending}
                      onClick={() => mutation.mutate('submit')}
                    >
                      Submit for approval
                    </Button>
                  )}
                  {data.status === 'submitted' && (
                    <Button
                      size="sm"
                      loading={mutation.isPending}
                      onClick={() => mutation.mutate('approve')}
                    >
                      Approve this step
                    </Button>
                  )}
                  {data.status === 'submitted' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!reasonValid}
                      onClick={() => mutation.mutate('reject')}
                    >
                      Reject
                    </Button>
                  )}
                  {['draft', 'submitted', 'scheduled'].includes(data.status) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!reasonValid}
                      onClick={() => mutation.mutate('cancel')}
                    >
                      Cancel
                    </Button>
                  )}
                  {canBypass && ['submitted', 'blocked'].includes(data.status) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!reasonValid}
                      onClick={() => mutation.mutate('bypass')}
                    >
                      Emergency bypass
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Approval is only accepted from the resolved approver for the current step.
                </p>
              </div>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
